import React, { useEffect, useState } from "react";
import Label from "../components/atoms/Label";
import Plan from "../components/templates/Plans";
import "../styles/SignUp.css";
import "../styles/PlanAndPricing.css";
import { usePricingPlans } from "../hooks/usePricingPlans";
import { useSelector } from "react-redux";
import PricingPlanTemplate from "./PricingPlanTemplate";
import { getCredits, isDowngradeAllowed } from "../services/PersonalDetailsApi";
import { logger } from "../utils/logger";
import { useLocation } from "react-router-dom";
import { set } from "lodash";

const PlansAndPricing = () => {
  const { planTierId: currentPlanTierId = '', emailId: loggedInUserEmailId } = useSelector((state) => state.userDetails);

  const { pricingPlans, isLoading, error } = usePricingPlans();

  const [action, setAction] = useState(null);
  const [viewPlanDetails, setViewPlanDetails] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [credits, setCredits] = useState(0);
  const [disableDowngrade, setDisableDowngrade] = useState(true);

  const location = useLocation();
  const emailId = location.state?.email || loggedInUserEmailId;
  const isFirstTimeSubscriber = location.state?.isFirstTimeSubscriber || false;

  useEffect(() => {
    fetchCredits();
    setDowngradeAccess();
  }, []); // add dependencies as needed

  const setDowngradeAccess = async () => {
    try {
      const canDowngrade = await isDowngradeAllowed(emailId);
      setDisableDowngrade(!canDowngrade);
    } catch (error) {
      console.error("Error checking downgrade access:", error);
    }
  };

  const fetchCredits = async () => {
    try {
      const currentCredits = await getCredits(emailId);
      setCredits(currentCredits);
    } catch (error) {
      console.error("Error fetching credits:", error);
    }
  };
  

	logger.log("pricingPlans", pricingPlans);

  if (isLoading) return (      
  <div
      className="loader-container"
      style={{
        height: "calc(100% - 60px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
  >
    <div className="loader"></div>
    <p>Loading...</p>
  </div>);

  if (error) return <div>{error}</div>;
  
  const currentPlanDetails = pricingPlans.find(plan => plan.planTierId === currentPlanTierId) || null;

  const handleButtonClick = (action, selectedTierId) => {
    setAction(action);
    setSelectedTierId(selectedTierId);
    setViewPlanDetails(true);
  };

  const showAllPlans = () => {
    setAction("");
    setSelectedTierId("");
    setViewPlanDetails(false);
  }

  const showPlanDetail = () => {
    setAction("VIEW_PLAN");
  }

  const getPlanBillingDetails = (planTierId, price) => {
    switch (planTierId) {
      case "QUICK_PREP":
        return { billingCycle: "1 month", billingRate: `$${price} per month` };
      case "GUIDED_LEARNER":
        return { billingCycle: "3 months", billingRate: `$${price} per quarter` };
      case "PEAK_PREP":
        return { billingCycle: "12 months", billingRate: `$${price} per year` };
      default:
        return { billingCycle: "Unknown duration", billingRate: "N/A" };
    }
  };
  

  const renderViewPlanComponent = () => {
    let selectedPlanDetails = pricingPlans.find(plan => plan.planTierId === selectedTierId);
    if (!selectedPlanDetails) return null;
    const { billingCycle, billingRate } = getPlanBillingDetails(selectedPlanDetails.planTierId, selectedPlanDetails.newPrice);
    return (
      <PricingPlanTemplate
        planName={selectedPlanDetails.heading}
        planShortDesc={selectedPlanDetails.subHeading + " - " + selectedPlanDetails.heading}
        price={selectedPlanDetails.newPrice}
        currentCredits={credits}
        discountClaimer = {`The discounted price applies for this billing cycle based on available credits. The ${selectedPlanDetails.heading} plan provides full access for ${billingCycle}. At the end of this period, the subscription will renew at the standard rate of ${billingRate} unless canceled or changed before the renewal date.`}
        features={selectedPlanDetails.features}
        limitations={selectedPlanDetails.limitations}
        isCurrentPlan={currentPlanDetails && currentPlanDetails.planTierId === selectedPlanDetails.planTierId}
        backClick={showAllPlans}
        emailId={emailId}
        planTierId={selectedPlanDetails.planTierId}
        isFirstTimeSubscriber={isFirstTimeSubscriber}
        stripePriceId={selectedPlanDetails.stripePriceId}
      />
    );
  };


  return (
  
    <div className="plan-pricing-container">
      <div
      >
        {!viewPlanDetails ? (
        <div style={{display : "flex" , flexDirection : "column",justifyContent : "center", alignItems :"center", gap:"14px", paddingTop : "2em" ,paddingBottom : "2em"}}>
        
        <>
          <Label
            label="Plans and Pricing"
            fontSize={"31px"}
            color={"#545454"}
            fontWeight={"700"}
          />

          <Label
            label="Find the plan that is right for you!"
            fontSize={"16px"}
            color={"#545454"}
            fontWeight={"400"}
          />
        </>
        </div>) : null}
        

        {!viewPlanDetails ? (
        <div
          className="plan-pricing" style={{overflowX : "auto",WebkitOverflowScrolling: 'touch'}}
        >
        {pricingPlans
            .filter((plan) => {
              // Exclude plans with a lower planType if disableDowngrade is true
              return !disableDowngrade || plan.planType >= currentPlanDetails?.planType;
            })
            .map((plan) => (
              <Plan
                key={plan.planTierId}
                heading={plan.heading}
                subHeading={plan.subHeading}
                amountLabel={plan.amountLabel}
                oldPriceLabel={plan.oldPriceLabel}
                planType={plan.planType}
                bgColor={plan.bgColor}
                features={plan.featuresShort}
                planTierId={plan.planTierId}
                currentPlanDetails={currentPlanDetails}
                onButtonClick={handleButtonClick}
                isFirstTimeSubscriber={isFirstTimeSubscriber}
              />
            ))}
        </div> ) : null}

        {viewPlanDetails && (
        <div className="plan-popup-container">
          {action === "DOWNGRADE" ? (
            <div className="plan-downgrade-container">
              <div style={{ fontSize: "25px", fontWeight: 700 }}>
                Do you want to downgrade?
              </div>
              <div style={{ fontSize: "20px" }}>
                You will be downgraded from some of the features you are getting currently
              </div>
              <div className="plan-downgrade-button-container">
                <div
                  className="plan-downgrade-actions"
                  style={{ fontSize: "20px" }}
                  onClick={showPlanDetail}
                >
                  Yes
                </div>
                <div
                  className="plan-downgrade-actions"
                  style={{ fontSize: "20px" }}
                  onClick={showAllPlans}
                >
                  No
                </div>
              </div>
            </div>
          ) : (action === "UPGRADE" || action === "VIEW_PLAN") ? (
            <div className="plan-view-container">
              {renderViewPlanComponent()}
            </div>
          ) : null}
        </div>
      )}
      </div>
    </div>
  );
};

export default PlansAndPricing;
